import { useLazyQuery } from '@apollo/client';
import { Select, Spin } from 'antd';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { USER_ROLES } from '../../../../../common/constants';
import { USERS } from '../../../../users/graphql/Queries';
import { TOY_RESERVATION_RULE_USER } from '../../graphql/Queries';

const { Option } = Select;

const USERS_LIMIT = 10;
const LOADING_OPTION_KEY = '__loading__';

const getUserName = (user) =>
  [user?.firstName, user?.lastName]
    ?.map((part) => part?.trim())
    ?.filter(Boolean)
    ?.join(' ');

export const getUserPrimaryLabel = (user) =>
  user?.userNumber || user?.contactNo || '-';

export const getUserSecondaryLabel = (user) =>
  [user?.contactNo, getUserName(user)]?.filter(Boolean)?.join(' - ');

export const getUserLabel = (user) => {
  const primary = getUserPrimaryLabel(user);
  const secondary = getUserSecondaryLabel(user);
  return secondary ? `${primary} · ${secondary}` : primary;
};

const ReservationUserSelect = ({
  value = [],
  onChange,
  placeholder = 'Search user by name, phone or user number',
  disabled = false,
  preloadUserIds = [],
}) => {
  const [options, setOptions] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const skipRef = useRef(0);
  const searchRef = useRef('');
  const requestIdRef = useRef(0);
  const labelMapRef = useRef({});
  const listRef = useRef(null);
  const preloadedIdsRef = useRef(new Set());

  const [fetchUsers] = useLazyQuery(USERS, {
    fetchPolicy: 'network-only',
    onError: () => {},
  });

  const [fetchUser] = useLazyQuery(TOY_RESERVATION_RULE_USER, {
    fetchPolicy: 'network-only',
    onError: () => {},
  });

  const buildOptions = (rows) =>
    (rows || [])?.map((user) => ({
      value: user?.id,
      label: getUserLabel(user),
      primary: getUserPrimaryLabel(user),
      secondary: getUserSecondaryLabel(user),
    }));

  const mergeOptions = (mapped, prepend = false) => {
    if (!mapped?.length) return;
    mapped?.forEach((option) => {
      labelMapRef.current[option?.value] = option?.label;
    });
    setOptions((prev) => {
      const existingIds = new Set(prev?.map((option) => option?.value));
      const fresh = mapped?.filter((option) => !existingIds?.has(option?.value));
      return prepend ? [...fresh, ...prev] : [...prev, ...fresh];
    });
  };

  const loadUsers = useCallback(
    async (skip = 0, searchVal = '') => {
      const requestId = requestIdRef.current;
      try {
        const result = await fetchUsers({
          variables: {
            filter: {
              skip,
              limit: USERS_LIMIT,
              search: searchVal || '',
              roles: [USER_ROLES?.USER?.key],
            },
            sort: { field: 'createdAt', order: 'DESC' },
          },
        });

        if (requestId !== requestIdRef.current) return;

        const rows = result?.data?.users?.data || [];
        const total = result?.data?.users?.count || 0;
        const mapped = buildOptions(rows);
        mapped?.forEach((option) => {
          labelMapRef.current[option?.value] = option?.label;
        });

        if (skip === 0) {
          setOptions((prev) => {
            const preloadedExtras = prev?.filter(
              (option) =>
                preloadedIdsRef.current?.has(option?.value) &&
                !mapped?.some((item) => item?.value === option?.value),
            );
            return [...mapped, ...preloadedExtras];
          });
        } else {
          mergeOptions(mapped);
        }
        setHasMore(skip + rows?.length < total);
      } catch (error) {
        if (requestId === requestIdRef.current) setHasMore(false);
      } finally {
        if (requestId === requestIdRef.current) {
          if (skip === 0) setIsInitialLoading(false);
          else setIsLoadingMore(false);
        }
      }
    },
    [fetchUsers],
  );

  useEffect(() => {
    skipRef.current = 0;
    setOptions([]);
    setHasMore(true);
    setIsInitialLoading(true);
    loadUsers(0, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const missingIds = (preloadUserIds || [])?.filter(
      (id) => id && !labelMapRef.current?.[id],
    );
    if (!missingIds?.length) return;

    Promise.all(
      missingIds?.map((id) =>
        fetchUser({ variables: { where: { id } } })
          .then((result) => result?.data?.user)
          .catch(() => null),
      ),
    )
      .then((users) => {
        const filtered = users?.filter(Boolean);
        filtered?.forEach((user) => preloadedIdsRef.current?.add(user?.id));
        mergeOptions(buildOptions(filtered), true);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(preloadUserIds)]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isLoadingMore || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [isLoadingMore]);

  const handleSearch = useCallback(
    debounce((searchValue) => {
      requestIdRef.current += 1;
      skipRef.current = 0;
      searchRef.current = searchValue;
      setOptions([]);
      setHasMore(true);
      setIsInitialLoading(true);
      loadUsers(0, searchValue);
    }, 500),
    [loadUsers],
  );

  useEffect(() => () => handleSearch?.cancel?.(), [handleSearch]);

  const handlePopupScroll = (event) => {
    const { target } = event;
    listRef.current = target;
    if (
      !isLoadingMore &&
      hasMore &&
      target?.scrollTop + target?.offsetHeight >= target?.scrollHeight - 20
    ) {
      const nextSkip = skipRef.current + USERS_LIMIT;
      skipRef.current = nextSkip;
      setIsLoadingMore(true);
      loadUsers(nextSkip, searchRef.current);
    }
  };

  let notFoundContent = 'No users found';
  if (isInitialLoading) {
    notFoundContent = <Spin size="small" />;
  }

  return (
    <Select
      mode="multiple"
      allowClear
      showSearch
      disabled={disabled}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      loading={isInitialLoading && options?.length === 0}
      onSearch={handleSearch}
      onPopupScroll={handlePopupScroll}
      filterOption={false}
      optionLabelProp="label"
      notFoundContent={notFoundContent}
    >
      {options?.map((option) => (
        <Option key={option?.value} value={option?.value} label={option?.label}>
          <div className="select-option-primary">{option?.primary}</div>
          {option?.secondary && (
            <div className="select-option-secondary">{option?.secondary}</div>
          )}
        </Option>
      ))}
      {isLoadingMore && (
        <Option key={LOADING_OPTION_KEY} value={LOADING_OPTION_KEY} disabled>
          <div className="select-load-more">
            <Spin size="small" />
          </div>
        </Option>
      )}
    </Select>
  );
};

export default ReservationUserSelect;
